// @givmo/app-charity — profile provenance + logo constraints (CPA-WEB6).
//
// The profile GET returns per-field provenance (`CharityFieldProvenanceOut[]`): for each tracked field,
// where the current value came from (`source`) and whether it was charity-entered (`is_human`). We surface
// a SUBTLE "you last edited this" affordance on human-edited fields — never a heavy audit UI.
//
// Also the client-side logo file constraints (the backend is authoritative; these are for fast feedback).

import type { CharityFieldProvenanceOut } from '@givmo/api-client';

/**
 * `accept` for the logo file picker. RASTER ONLY, and deliberately so: the backend's
 * `ALLOWED_IMAGE_MIMES` excludes `image/svg+xml` because storing attacker-controlled SVG on a
 * public bucket is a stored-XSS vector. Offering SVG here only produced a pick that sailed past
 * the client check and was rejected by the server — keep this set == the backend allow-list.
 */
export const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp';
/** Client-side size guard (backend is authoritative). Logos are small; keep well under the upload cap. */
export const LOGO_MAX_BYTES = 5 * 1024 * 1024; // 5 MB
export const LOGO_TOO_LARGE_MESSAGE = 'That image is over 5 MB. Please choose a smaller logo.';
export const LOGO_WRONG_TYPE_MESSAGE = 'Please choose a PNG, JPG, or WebP image.';

/** Index provenance rows by field name for O(1) lookup. Tolerant of a missing/empty list. */
export function provenanceByField(
  provenance: readonly CharityFieldProvenanceOut[] | undefined,
): Map<string, CharityFieldProvenanceOut> {
  const map = new Map<string, CharityFieldProvenanceOut>();
  for (const row of provenance ?? []) {
    if (typeof row.field === 'string') map.set(row.field, row);
  }
  return map;
}

/**
 * Whether a field's current value was charity-entered (human), so we can show the subtle "you edited
 * this" hint. Fields with no provenance row, or a machine/import source, return false.
 */
export function isHumanEdited(
  byField: Map<string, CharityFieldProvenanceOut>,
  field: string,
): boolean {
  return byField.get(field)?.is_human === true;
}

/** True when the picked file looks like an image we accept (fast client check; backend re-validates). */
export function isAcceptableLogo(file: File): boolean {
  const acceptedTypes = new Set(LOGO_ACCEPT.split(','));
  return acceptedTypes.has(file.type);
}
